/**
 * EduSaas — Reporter d'erreurs JS frontend (P1-7, 6.1.4b),
 * module 8.A.3 (ex `public/js/core/error-reporter.js`).
 *
 * Capture les erreurs non gérées et les rejets de promesses, POST
 * /api/client-errors (log Pino serveur). Throttle + dédupe ; best-effort.
 *
 * Nuance 8.A.3 : installé à l'exécution du shell (module deferred) — les
 * erreurs de PARSE des scripts classiques précédents ne sont plus couvertes
 * (fenêtre minuscule : ils ne font que définir des fonctions). Les erreurs
 * runtime (post-init) restent toutes captées, comme avant.
 */

interface ErrorReport {
  kind: string;
  message: string;
  source?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  url: string;
  userAgent: string;
}

const MAX_REPORTS = 20; // plafond par chargement de page
let sent = 0;
const seen = new Set<string>(); // dédupe par signature

function report(payload: ErrorReport): void {
  if (sent >= MAX_REPORTS) return;
  const sig = `${payload.message}|${payload.source || ''}|${payload.lineno || ''}`;
  if (seen.has(sig)) return;
  seen.add(sig);
  sent++;
  try {
    fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true, // survit à un unload de page
      body: JSON.stringify(payload),
    }).catch(() => {
      /* best-effort : ne jamais re-reporter */
    });
  } catch {
    /* ignore */
  }
}

export function installErrorReporter(): void {
  window.addEventListener('error', (event) => {
    if (!event || (!event.message && !event.error)) return;
    const err: unknown = event.error;
    const errMessage = err instanceof Error ? err.message : undefined;
    const errStack = err instanceof Error ? err.stack : undefined;
    report({
      kind: 'error',
      message: String(event.message || errMessage || 'Unknown error').slice(0, 1000),
      source: event.filename ? String(event.filename).slice(0, 1000) : undefined,
      lineno: typeof event.lineno === 'number' ? event.lineno : undefined,
      colno: typeof event.colno === 'number' ? event.colno : undefined,
      stack: errStack ? String(errStack).slice(0, 4000) : undefined,
      url: location.href.slice(0, 1000),
      userAgent: navigator.userAgent.slice(0, 500),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event?.reason;
    const message =
      reason instanceof Error ? reason.message : reason ? String(reason) : 'Unhandled rejection';
    report({
      kind: 'unhandledrejection',
      message: String(message).slice(0, 1000),
      stack: reason instanceof Error && reason.stack ? reason.stack.slice(0, 4000) : undefined,
      url: location.href.slice(0, 1000),
      userAgent: navigator.userAgent.slice(0, 500),
    });
  });
}
